import { useMemo, useState } from 'react'

import type { SupportAppRead } from '@/shared/api/contracts'
import { Button, MultiSelect } from '@/shared/components/ui'
import { useAssignableUsers } from '@/shared/hooks/use-users'
import { useT } from '@/shared/i18n'
import type { SupportLeader } from '@/shared/types/domain'

import { useSetSupportLeaders, useSupportLeaders } from '../api'

export interface LeadersBarProps {
  apps: readonly SupportAppRead[]
  /** Admin: puede cambiar la lista. El backend lo exige igual. */
  canEdit: boolean
}

/**
 * A quién le llega el aviso de cada reporte que necesita un programador.
 *
 * Siempre al responsable del proyecto de soporte de la app, y además a los
 * líderes de la empresa. Es una lista y no una persona fija: hoy es el líder
 * del área y mañana pueden ser otras personas, sin tocar código.
 */
export function LeadersBar({ apps, canEdit }: LeadersBarProps) {
  const leaders = useSupportLeaders()

  // Una fila por empresa con apps dadas de alta (hoy, una sola).
  const porEmpresa = useMemo(() => {
    const grupos = new Map<string, SupportAppRead[]>()
    for (const app of apps) grupos.set(app.company_id, [...(grupos.get(app.company_id) ?? []), app])
    return [...grupos.entries()]
  }, [apps])

  if (porEmpresa.length === 0) return null

  return (
    <div className="hairline-b flex flex-col gap-1 bg-surface-1 px-4 py-2">
      {porEmpresa.map(([companyId, appsDeLaEmpresa]) => (
        <LeadersRow
          key={companyId}
          companyId={companyId}
          apps={appsDeLaEmpresa}
          leaders={(leaders.data ?? []).filter((leader) => leader.companyId === companyId)}
          canEdit={canEdit}
        />
      ))}
    </div>
  )
}

interface LeadersRowProps {
  companyId: string
  apps: readonly SupportAppRead[]
  leaders: readonly SupportLeader[]
  canEdit: boolean
}

function LeadersRow({ companyId, apps, leaders, canEdit }: LeadersRowProps) {
  const t = useT()
  const [editing, setEditing] = useState(false)
  const [elegidos, setElegidos] = useState<string[]>([])
  const guardar = useSetSupportLeaders()
  // Los que pueden recibir los avisos: la misma regla que el responsable de una
  // tarea de esa empresa (el proyecto de soporte de cualquiera de sus apps).
  const asignables = useAssignableUsers(editing ? apps[0]!.project_id : null)

  const nombres = leaders.map((leader) => leader.user.display_name).join(', ')
  const opciones = (asignables.data ?? []).map((user) => ({
    value: user.id,
    label: user.display_name,
    text: user.display_name,
  }))

  const empezar = () => {
    setElegidos(leaders.map((leader) => leader.user.id))
    setEditing(true)
  }

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="text-text-3">
        {t('support.leaders.description', { apps: apps.map((app) => app.name).join(', ') })}
      </span>
      {editing ? (
        <>
          <MultiSelect
            label={t('support.leaders.picker')}
            placeholder={t('support.leaders.placeholder')}
            options={opciones}
            values={elegidos}
            onChange={setElegidos}
            disabled={asignables.isPending}
            className="w-64"
          />
          <Button
            variant="primary"
            disabled={guardar.isPending}
            onClick={() =>
              guardar.mutate(
                { company_id: companyId, user_ids: elegidos },
                { onSuccess: () => setEditing(false) },
              )
            }
          >
            {t('support.leaders.save')}
          </Button>
          <Button variant="ghost" onClick={() => setEditing(false)}>
            {t('support.leaders.cancel')}
          </Button>
        </>
      ) : (
        <>
          <span className="text-text-1">{nombres || t('support.leaders.none')}</span>
          {canEdit ? (
            <Button variant="ghost" onClick={empezar}>
              {t('support.leaders.edit')}
            </Button>
          ) : null}
        </>
      )}
    </div>
  )
}
